import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

function createInscriptionCanvas(data, callback) {
  var img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = function() {
    var size = 1024;
    var c = document.createElement("canvas");
    c.width = size; c.height = size;
    var ctx = c.getContext("2d");

    // Draw crystal photo scaled to fill
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, size, size);
    var scale = Math.max(size / img.width, size / img.height);
    var w = img.width * scale, h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);

    // Switch to lighten blend — text glows through dark areas
    ctx.globalCompositeOperation = "lighten";

    var cx = 512;
    ctx.textAlign = "center";

    // Crown
    ctx.font = "28px Georgia"; ctx.fillStyle = "rgba(201,169,110,0.5)";
    ctx.fillText("\u2726", cx, 180);

    // Subtitle
    ctx.font = "8px sans-serif"; ctx.fillStyle = "rgba(201,169,110,0.3)";
    ctx.fillText("B U I L T   B Y   T H O S E   W H O   K N O W   Y O U", cx, 205);

    // Coachlines
    ctx.globalCompositeOperation = "lighten";
    ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 0.6;
    ctx.beginPath(); ctx.moveTo(250, 222); ctx.lineTo(498, 222); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(526, 222); ctx.lineTo(774, 222); ctx.stroke();
    ctx.fillStyle = "rgba(201,169,110,0.3)";
    ctx.save(); ctx.translate(512, 222); ctx.rotate(Math.PI/4);
    ctx.fillRect(-4, -4, 8, 8); ctx.restore();

    // Star labels
    var stars = data.stars || [];
    var labels = stars.slice(0, 6).map(function(s) { return s.label || ""; });
    var startY = 280;
    var spacing = labels.length <= 4 ? 55 : labels.length <= 5 ? 48 : 42;
    labels.forEach(function(label, i) {
      var op = i === 0 ? 0.7 : i < 3 ? 0.55 : 0.42;
      ctx.font = "italic 24px Georgia";
      ctx.fillStyle = "rgba(255,255,255," + op + ")";
      ctx.fillText(label, cx, startY + i * spacing);
      if (i < labels.length - 1) {
        ctx.fillStyle = "rgba(201,169,110,0.15)";
        ctx.beginPath(); ctx.arc(cx, startY + i * spacing + spacing * 0.45, 2, 0, Math.PI * 2); ctx.fill();
      }
    });

    // Bottom coachline
    var divY = startY + (labels.length - 1) * spacing + 32;
    ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 0.6;
    ctx.beginPath(); ctx.moveTo(230, divY); ctx.lineTo(498, divY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(526, divY); ctx.lineTo(794, divY); ctx.stroke();
    ctx.fillStyle = "rgba(201,169,110,0.3)";
    ctx.save(); ctx.translate(512, divY); ctx.rotate(Math.PI/4);
    ctx.fillRect(-4, -4, 8, 8); ctx.restore();

    // Name
    ctx.font = "italic 46px Georgia"; ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.fillText(data.personName || "", cx, divY + 52);

    // Tagline
    ctx.font = "italic 16px Georgia"; ctx.fillStyle = "rgba(201,169,110,0.45)";
    ctx.fillText(data.headline || "", cx, divY + 80);

    var texture = new THREE.CanvasTexture(c);
    texture.needsUpdate = true;
    callback(texture);
  };
  img.src = "/crystal-ref.png";
}

export default function CrystalViewer3D() {
  var mountRef = useRef(null);
  var rotRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0, dragging: false });
  var mouseRef = useRef({ x: 0, y: 0 });
  var [dragging, setDragging] = useState(false);
  var [darkMode, setDarkMode] = useState(true);

  var sampleData = {
    personName: "James Whitfield",
    headline: "The man who made the team the hero",
    stars: [
      { label: "steady hand" }, { label: "quiet credit" }, { label: "crisis calm" },
      { label: "people sight" }, { label: "selfless lead" }, { label: "team builder" },
    ],
  };

  useEffect(function() {
    if (!mountRef.current) return;
    var container = mountRef.current;
    var w = container.clientWidth, h = container.clientHeight;

    var scene = new THREE.Scene();
    scene.background = new THREE.Color(darkMode ? 0x0E0B18 : 0xF0EDE8);

    var camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 100);
    camera.position.set(0, 0.1, 10);
    camera.lookAt(0, 0, 0);

    var renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = darkMode ? 1.3 : 2.0;
    container.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0x404060, darkMode ? 0.4 : 0.6));
    var keyLight = new THREE.DirectionalLight(0xffffff, darkMode ? 0.8 : 1.2);
    keyLight.position.set(3, 4, 5); scene.add(keyLight);
    var rimLight = new THREE.DirectionalLight(0xccccff, 0.4);
    rimLight.position.set(-2, 3, -3); scene.add(rimLight);

    var crystalGroup = new THREE.Group();

    // Create the crystal as a thin octagonal slab
    var shape = new THREE.Shape();
    var R = 2.2;
    for (var i = 0; i < 8; i++) {
      var angle = (i * Math.PI * 2) / 8 - Math.PI / 8;
      if (i === 0) shape.moveTo(R * Math.cos(angle), R * Math.sin(angle));
      else shape.lineTo(R * Math.cos(angle), R * Math.sin(angle));
    }
    shape.closePath();
    var crystalGeo = new THREE.ExtrudeGeometry(shape, {
      depth: 0.25, bevelEnabled: true, bevelThickness: 0.06,
      bevelSize: 0.04, bevelSegments: 2,
    });
    crystalGeo.center();

    // Edge material — semi-transparent crystal edges
    var edgeMat = new THREE.MeshPhysicalMaterial({
      color: darkMode ? 0xaabbcc : 0xdddde8,
      transparent: true,
      opacity: 0.35,
      metalness: 0.1,
      roughness: 0.05,
      clearcoat: 1.0,
      clearcoatRoughness: 0.02,
      side: THREE.DoubleSide,
    });
    crystalGroup.add(new THREE.Mesh(crystalGeo, edgeMat));

    // Bright edges for sparkle
    var edgesGeo = new THREE.EdgesGeometry(crystalGeo, 12);
    var edgeLineMat = new THREE.LineBasicMaterial({
      color: darkMode ? 0xffffff : 0x999999, transparent: true, opacity: 0.3,
    });
    crystalGroup.add(new THREE.LineSegments(edgesGeo, edgeLineMat));

    // Load crystal photo + text composite as front face texture
    createInscriptionCanvas(sampleData, function(texture) {
      // Front face — the real crystal photo with text
      var frontGeo = new THREE.PlaneGeometry(4.0, 4.0);
      var frontMat = new THREE.MeshBasicMaterial({
        map: texture, transparent: true, side: THREE.FrontSide,
      });
      var frontMesh = new THREE.Mesh(frontGeo, frontMat);
      frontMesh.position.z = 0.14;
      crystalGroup.add(frontMesh);

      // Back face — dimmer mirror
      var backMat = new THREE.MeshBasicMaterial({
        map: texture, transparent: true, opacity: 0.3, side: THREE.FrontSide,
      });
      var backMesh = new THREE.Mesh(frontGeo.clone(), backMat);
      backMesh.position.z = -0.14;
      backMesh.rotation.y = Math.PI; // flip to face back
      crystalGroup.add(backMesh);
    });

    scene.add(crystalGroup);

    // Animation
    var animId;
    var animate = function() {
      animId = requestAnimationFrame(animate);
      rotRef.current.x += (rotRef.current.targetX - rotRef.current.x) * 0.06;
      rotRef.current.y += (rotRef.current.targetY - rotRef.current.y) * 0.06;
      crystalGroup.rotation.y = rotRef.current.y;
      crystalGroup.rotation.x = Math.max(-0.2, Math.min(0.2, rotRef.current.x));
      edgeLineMat.opacity = 0.25 + Math.abs(Math.sin(rotRef.current.y * 2)) * 0.2;
      renderer.render(scene, camera);
    };
    animate();

    var autoRotate = setInterval(function() {
      if (!rotRef.current.dragging) rotRef.current.targetY += 0.003;
    }, 16);

    var onResize = function() {
      var nw = container.clientWidth, nh = container.clientHeight;
      camera.aspect = nw / nh; camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener("resize", onResize);

    return function() {
      cancelAnimationFrame(animId); clearInterval(autoRotate);
      window.removeEventListener("resize", onResize);
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [darkMode]);

  var onDown = function(e) {
    setDragging(true); rotRef.current.dragging = true;
    var cx = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    var cy = e.clientY || (e.touches && e.touches[0].clientY) || 0;
    mouseRef.current = { x: cx, y: cy };
  };
  var onMove = function(e) {
    if (!rotRef.current.dragging) return;
    var cx = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    var cy = e.clientY || (e.touches && e.touches[0].clientY) || 0;
    rotRef.current.targetY += (cx - mouseRef.current.x) * 0.008;
    rotRef.current.targetX += (cy - mouseRef.current.y) * 0.004;
    mouseRef.current = { x: cx, y: cy };
  };
  var onUp = function() { setDragging(false); rotRef.current.dragging = false; };

  return (
    <div style={{ width: "100%", height: "100vh", background: darkMode ? "#0E0B18" : "#F0EDE8", position: "relative", cursor: dragging ? "grabbing" : "grab", touchAction: "none", transition: "background 0.4s" }}>
      <div ref={mountRef} style={{ width: "100%", height: "100%" }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
      />
      <button onClick={function() { setDarkMode(!darkMode); }} style={{
        position: "absolute", top: 28, right: 32,
        background: "none", border: "1px solid " + (darkMode ? "rgba(232,230,226,0.1)" : "rgba(26,23,32,0.1)"),
        padding: "8px 18px", cursor: "pointer",
        fontFamily: "'Outfit',sans-serif", fontSize: 9, fontWeight: 300,
        letterSpacing: 4, color: darkMode ? "rgba(232,230,226,0.3)" : "rgba(26,23,32,0.3)",
        textTransform: "uppercase",
      }}>{darkMode ? "light" : "dark"}</button>
      <div style={{
        position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
        color: darkMode ? "rgba(232,230,226,0.2)" : "rgba(26,23,32,0.2)",
        fontFamily: "'Outfit',sans-serif", fontSize: 10, letterSpacing: 5,
        textTransform: "uppercase", pointerEvents: "none",
      }}>Drag to rotate</div>
    </div>
  );
}
